import React from "react";
import Divider from "@mui/material/Divider";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import DesktopWindowsOutlinedIcon from "@mui/icons-material/DesktopWindowsOutlined";
import DepartmentSettingsDisplay from "./DepartmentSettingsDisplay";
import RoleSettingsDisplay from "./RoleSettingsDisplay";
import StationSettingsDisplay from "./StationSettingsDisplay";
import LoadSpinner from "../../../utilities/LoadSpinner";
import {
  API_get_departments,
  API_get_roles,
  API_get_stations,
  API_get_role_delete_impact,
  API_get_role_type_delete_impact,
  API_put_role_type,
  API_put_role,
  API_put_station,
  API_list_pos_roles,
  API_create_role_from_pos_role,
  API_link_pos_role,
  API_archive_pos_role,
  API_role_set_order,
} from "../../../constants";
import {
  getRightworkAPI,
  postRightworkAPI,
} from "../requests/utilities/requests";
import { getDefaultAxiosConfig } from "../../../utilities/requests";
import axios from "axios";
import TopBarDisplayTitle from "../home/top_bar_components/TopBarDisplayTitle";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CompanyNameSidebar from "../home/top_bar_components/CompanyNameSidebar";
import { useOnboardingIntent } from "../onboarding/onboardingNavigation";

function DepartmentRoleStationSettingsDisplay(props) {
  const [data, setData] = React.useState([]);
  const [display, setDisplay] = React.useState("Departments");
  const [loadError, setLoadError] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [isSavingData, setIsSavingData] = React.useState(false);
  const [openRoleImportModal, setOpenRoleImportModal] = React.useState(false);

  const companyOptions = React.useMemo(() => {
    const unique = [];
    props.storeNames?.forEach((s) => {
      if (!unique.some((c) => c.id === s.company)) {
        unique.push({
          id: s.company,
          name: s.company_info?.company_name || s.company_info?.name,
          ...s.company_info,
        });
      }
    });
    return unique;
  }, [props.storeNames]);

  const [selectedCompanyId, setSelectedCompanyId] = React.useState(() => {
    if (props.homeCompany) {
      return props.homeCompany;
    }
    return companyOptions[0]?.id;
  });

  React.useEffect(() => {
    if (!selectedCompanyId && companyOptions[0]?.id) {
      setSelectedCompanyId(companyOptions[0].id);
    }
  }, [companyOptions, selectedCompanyId]);

  React.useEffect(() => {
    if (!props.homeCompany) {
      return;
    }
    setSelectedCompanyId((current) => {
      if (current === props.homeCompany) {
        return current;
      }
      return props.homeCompany;
    });
  }, [props.homeCompany]);

  const handleCompanyChange = React.useCallback(
    (companyId) => {
      setSelectedCompanyId(companyId);
      const companyStores = props.storeNames?.filter((s) => s.company === companyId) || [];
      const hasCurrentStore = companyStores.some((s) => s.id === props.homeStore);
      const fallbackStoreId = hasCurrentStore
        ? props.homeStore
        : companyStores[0]?.id;
      if (fallbackStoreId && fallbackStoreId !== props.homeStore) {
        props.change_home_store(fallbackStoreId);
      }
    },
    [props.storeNames, props.homeStore, props.change_home_store]
  );

  const effectiveCompanyId = selectedCompanyId || props.homeCompany;

  const axiosConfig = getDefaultAxiosConfig();

  const default_api_calls = {
    departments: API_get_departments,
    roles: API_get_roles,
    stations: API_get_stations,
    posRoles: API_list_pos_roles
  };

  const api_params = {
    store_id: props.homeStore,
    company_id: props.homeCompany,
    ...(props.companyScope ? { company_scope: true } : {}),
  };

  const fetch_data = async (api_calls, api_params, showLoading) => {
    setIsLoading(showLoading);
    setLoadError("");
    let api_set_data_name = Object.keys(api_calls);
    let api_list = Object.values(api_calls);
    try {
      // console.log("CALLING APIS");
      const res = await Promise.all(
        api_list.map((api) => getRightworkAPI(api, api_params))
      );
      var tempData = { ...data };
      res.map((res, i) => (tempData[api_set_data_name[i]] = res.data));
      setData(tempData);
      setIsLoading(false);
      setIsSavingData(false);
    } catch (error) {
      setLoadError("Couldn’t load team settings. Please try again.");
      setIsLoading(false);
      setIsSavingData(false);
    }
  };

  // ----------- POST APIs ----------- //
  const edit_department = async (data, edit_type, replacement_role_type_id) => {
    // Prepare data to send
    let requestData = { ...data, store_id: props.homeStore };
    if (replacement_role_type_id) {
      requestData.replacement_role_type_id = replacement_role_type_id;
    }
    setIsSavingData(true);

    try {
      if (edit_type === "delete") {
        toast.loading("Deleting Department...", { autoClose: false });
      }

      const res = await axios.put(
        `${API_put_role_type}${props.homeStore}&edit_type=${edit_type}`,
        requestData,
        axiosConfig
      );

      if (edit_type === "delete") {
        toast.dismiss();
        toast.success("Department Deleted Successfully", { autoClose: 3000 });
      }

      fetch_data(default_api_calls, api_params, false);
      return true;
    } catch (error) {
      if (edit_type === "delete") {
        toast.dismiss();

        if (error.response?.data?.system?.message) {
          toast.error(error.response?.data?.system?.message, {
            autoClose: 3000,
          });
          // console.log("Error Info", error.response?.data?.system?.data);
        } else {
          toast.error("Request Failed", { autoClose: 3000 });
        }
      } else {
        alert("Something went wrong, please try again later.");
      }

      console.error("Error:", error); // Handle error as needed
      return false;
    } finally {
      setIsSavingData(false);
    }
  };

  const edit_role = async (data, color, edit_type, replacement_role_id) => {
    // Prepare data to send
    let requestData = { ...data, store_id: props.homeStore, color: color, ...(props.companyScope ? { company_scope: true } : {}) };
    if (replacement_role_id) {
      requestData.replacement_role_id = replacement_role_id;
    }
    setIsSavingData(true);

    try {
      if (edit_type === "delete") {
        toast.loading("Deleting Role...", { autoClose: false });
      }

      const res = await axios.put(
        `${API_put_role}${props.homeStore}&edit_type=${edit_type}`,
        requestData,
        axiosConfig
      );

      if (edit_type === "delete") {
        toast.dismiss();
        toast.success("Role Deleted Successfully", { autoClose: 3000 });
      }

      fetch_data(default_api_calls, api_params, false);
      return true;
    } catch (error) {
      if (edit_type === "delete") {
        toast.dismiss();

        if (error.response?.data?.system?.message) {
          toast.error(error.response?.data?.system?.message, {
            autoClose: 3000,
          });
          // console.log("Error Info", error.response?.data?.system?.data);
        } else {
          toast.error("Request Failed", { autoClose: 3000 });
        }
      } else {
        alert("Something went wrong, please try again later.");
      }

      console.error("Error:", error); // Handle error as needed
      return false;
    } finally {
      setIsSavingData(false);
    }
  };

  const role_set_order = async (role_settings) => {
    const requestData = {
      store_id: props.homeStore,
      role_settings: JSON.stringify(role_settings),
    };
    try {
      toast.loading("Saving Role Order...", { autoClose: false });
      const res = await postRightworkAPI(API_role_set_order, requestData);
      if (res && res.status === 200) {
        toast.dismiss();
        toast.success("Order Saved", { autoClose: 3000 });
        fetch_data({ roles: API_get_roles }, api_params, false);
        return true;
      }
      toast.dismiss();
      toast.error("Request Failed", { autoClose: 3000 });
      return false;
    } catch (error) {
      toast.dismiss();
      toast.error("Request Failed", { autoClose: 3000 });
      console.error("Error:", error);
      return false;
    }
  };

  const get_role_delete_impact = async (role_id) => {
    try {
      const res = await getRightworkAPI(API_get_role_delete_impact, {
        store_id: props.homeStore,
        role_id,
      });
      return res?.data?.redux?.payload || res?.data;
    } catch (error) {
      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Unable to load delete impact", { autoClose: 3000 });
      }
      return null;
    }
  };

  const get_department_delete_impact = async (role_type_id) => {
    try {
      const res = await getRightworkAPI(API_get_role_type_delete_impact, {
        store_id: props.homeStore,
        role_type_id,
      });
      return res?.data?.redux?.payload || res?.data;
    } catch (error) {
      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Unable to load delete impact", { autoClose: 3000 });
      }
      return null;
    }
  };

  const edit_station = async (data, edit_type) => {
    // Prepare data to send
    let requestData = { ...data, store_id: props.homeStore };
    setIsSavingData(true);

    try {
      if (edit_type === "delete") {
        toast.loading("Deleting Station...", { autoClose: false });
      }

      const res = await axios.put(
        `${API_put_station}${props.homeStore}&edit_type=${edit_type}`,
        requestData,
        axiosConfig
      );

      if (edit_type === "delete") {
        toast.dismiss();
        toast.success("Station Deleted Successfully", { autoClose: 3000 });
      }

      fetch_data(default_api_calls, api_params, false);
    } catch (error) {
      if (edit_type === "delete") {
        toast.dismiss();

        if (error.response?.data?.system?.message) {
          toast.error(error.response?.data?.system?.message, {
            autoClose: 3000,
          });
          // console.log("Error Info", error.response?.data?.system?.data);
        } else {
          toast.error("Request Failed", { autoClose: 3000 });
        }
      } else {
        alert("Something went wrong, please try again later.");
      }

      console.error("Error:", error); // Handle error as needed
    } finally {
      setIsSavingData(false);
    }
  };

  // create_role_from_pos_role (POST)
  // - pos_role_uuid
  // - company_id
  const create_role_from_pos_role = async (
    pos_role_uuid,
    role_type_id,
  ) => {
    // Prepare data to send
    const requestData = {
      company_id: effectiveCompanyId,
      pos_role_uuid: pos_role_uuid,
      role_type_id: role_type_id,
    };


    try {
      toast.loading("Creating Role...", { autoClose: false });

      const res = await postRightworkAPI(
        API_create_role_from_pos_role,
        requestData
      );

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Role Created!", { autoClose: 3000 });
        fetch_data(
          {
            roles: API_get_roles,
            posRoles: API_list_pos_roles
          },
          api_params,
          false
        );
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
        // console.log("ERROR MESSAGE", error.response?.data?.system);
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed
    }
  };

  // [POST] api/pos_info/link_pos_role
  // * company_id (auth)
  // * pos_role_uuid
  // * role_id
  const link_role_from_pos = async (
    pos_role_uuid,
    role_id
  ) => {
    // Prepare data to send
    const requestData = {
      company_id: effectiveCompanyId,
      pos_role_uuid: pos_role_uuid,
      role_id: role_id
    };


    try {
      toast.loading("Linking Roles...", { autoClose: false });

      const res = await postRightworkAPI(
        API_link_pos_role,
        requestData
      );

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Roles Linked!", { autoClose: 3000 });
        fetch_data(
          {
            roles: API_get_roles,
            posRoles: API_list_pos_roles
          },
          api_params,
          false
        );
        return true;
      }

      toast.dismiss();
      toast.error("Request Failed", { autoClose: 3000 });
      return false;
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
        // console.log("ERROR MESSAGE", error.response?.data?.system);
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed
      return false;
    }
  };

    // [POST] api/pos_info/archive_pos_role
  // * company_id (auth)
  // * pos_role_uuid
  const archive_role_from_pos = async (
    pos_role_uuid,
  ) => {
    // Prepare data to send
    const requestData = {
      company_id: effectiveCompanyId,
      pos_role_uuid: pos_role_uuid,
    };

    try {
      toast.loading("Excluding Role...", { autoClose: false });

      const res = await postRightworkAPI(
        API_archive_pos_role,
        requestData
      );

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Role Excluded!", { autoClose: 3000 });
        fetch_data(
          {
            roles: API_get_roles,
            posRoles: API_list_pos_roles
          },
          api_params,
          false
        );
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
        // console.log("ERROR MESSAGE", error.response?.data?.system);
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed
    }
  };


  React.useEffect(() => {
    fetch_data(default_api_calls, api_params, true);
  }, [props.homeStore, props.homeCompany]);

  // Refetch roles (and POS roles) after the assistant maps a POS job to a
  // role, so the Roles tab reflects the change without a manual reload.
  // Seeded at mount so a stale signal does not refire on navigation.
  const opsRefreshTimestamp = props.opsRefreshSignal?.timestamp;
  const lastOpsRefreshRef = React.useRef(opsRefreshTimestamp);
  React.useEffect(() => {
    if (!opsRefreshTimestamp || opsRefreshTimestamp === lastOpsRefreshRef.current) {
      return;
    }
    lastOpsRefreshRef.current = opsRefreshTimestamp;
    if (!props.homeStore || !props.opsRefreshSignal) return;
    const types = props.opsRefreshSignal.types || [];
    if (types.includes("roles")) {
      fetch_data(default_api_calls, api_params, false);
    }
  }, [opsRefreshTimestamp]);

  useOnboardingIntent(props.embedded && !props.companyScope ? `inline-team-structure-${props.homeStore}` : "departmentRoleStationSettings", (intent) => {
    if (intent.intent === "import_roles" || intent.intent === "suggest_pos_mappings") {
      setDisplay("Roles");
      setOpenRoleImportModal(intent.intent === "suggest_pos_mappings" ? "suggest" : true);
    } else if (intent.intent === "roles") {
      // Setup Assistant follow-along: show the roles list without the POS
      // import modal (the assistant is creating the roles itself).
      setDisplay("Roles");
    } else if (intent.intent === "departments") {
      setDisplay("Departments");
    }
  }, props.active !== false);

  // console.log(data);

  const store_info = props.storeNames.find((s) => s.id === props.homeStore);
  const store_name = store_info ? store_info["store_name"] : "";


  const posRoles = data?.posRoles?.redux?.payload?.pos_roles

  // console.log("POS ROLES", posRoles)

  return (
    <>
      {loadError && <p role="alert" className="rounded-lg bg-rose-50 p-4 text-sm text-rose-700">{loadError}<button className="ml-2 underline" onClick={() => fetch_data(default_api_calls, api_params, true)}>Try again</button></p>}
      {!isLoading ? (
        <div className={props.embedded ? "w-full" : "w-full min-h-screen flex"}>
          {!props.embedded && <div className="w-1/5">
            <div className="h-full flex flex-col gap-2 px-6 py-5 bg-slate-50 border-r border-slate-200">
              <div className="mb-2">
                <CompanyNameSidebar
                  companyOptions={companyOptions}
                  selectedCompanyId={selectedCompanyId}
                  setSelectedCompanyId={handleCompanyChange}
                />
              </div>
              {/* <div>
                <StoreNameSidebar
                  storeOptions={props.storeOptions}
                  homeStore={props.homeStore}
                  storeNames={props.storeNames}
                  change_home_store={props.change_home_store}
                />
              </div> */}
              {/* <button
          className={`mt-12 w-full py-3 px-4 text-left text-base flex items-center relative rounded-2xl ${
            display === "Import" ? "bg-white" : ""
          }`}
          onClick={() => setDisplay("Import")}
        >
          <div
            className={`flex flex-1 items-center space-x-4 ${
              display === "Import"
                ? "text-violet-700"
                : "text-slate-500"
            }`}
          >
            <div>
              <FileDownloadOutlinedIcon />
            </div>
            <span>Import Settings</span>
          </div>
          {shiftPoolRequests?.length > 0 && (
            <span className="text-slate-500 rounded-full flex items-center justify-center text-sm">
              {shiftPoolRequests?.length}
            </span>
          )}
        </button> */}
              {/* <div className="mt-8">
          <Divider />
        </div> */}
              <button
                className={`w-full py-3 px-4 text-left text-base flex items-center relative rounded-2xl ${
                  display === "Departments" ? "bg-white" : ""
                }`}
                onClick={() => setDisplay("Departments")}
              >
                <div
                  className={`flex flex-1 items-center space-x-4 ${
                    display === "Departments"
                      ? "text-violet-700"
                      : "text-slate-500"
                  }`}
                >
                  <div>
                    <FolderOpenOutlinedIcon />
                  </div>
                  <span>Departments</span>
                </div>
                {/* {shiftPoolRequests?.length > 0 && (
            <span className="text-slate-500 rounded-full flex items-center justify-center text-sm">
              {shiftPoolRequests?.length}
            </span>
          )} */}
              </button>
              <button
                className={`w-full py-3 px-4 text-left flex items-center relative rounded-2xl ${
                  display === "Roles" ? "bg-white" : ""
                }`}
                onClick={() => setDisplay("Roles")}
              >
                <div
                  className={`flex flex-1 items-center space-x-4 ${
                    display === "Roles" ? "text-violet-700" : "text-slate-500"
                  }`}
                >
                  <div>
                    <PeopleOutlinedIcon />
                  </div>
                  <span>Roles</span>
                </div>
                {/* {availabilityRequests?.pending?.length > 0 && (
            <span className="text-slate-500 rounded-full flex items-center justify-center text-sm">
              {availabilityRequests?.pending?.length}
            </span>
          )} */}
              </button>
              <button
                className={`w-full py-3 px-4 text-left flex items-center relative rounded-2xl ${
                  display === "Stations" ? "bg-white" : ""
                }`}
                onClick={() => setDisplay("Stations")}
              >
                <div
                  className={`flex flex-1 items-center space-x-4 ${
                    display === "Stations"
                      ? "text-violet-700"
                      : "text-slate-500"
                  }`}
                >
                  <div>
                    <DesktopWindowsOutlinedIcon />
                  </div>
                  <span>Stations</span>
                </div>
                {/* {timeOffRequests?.length > 0 && (
            <span className="text-slate-500 rounded-full flex items-center justify-center text-sm">
              {timeOffRequests?.length}
            </span>
          )} */}
              </button>
            </div>
          </div>}
          <div className={props.embedded ? "w-full min-w-0" : "w-4/5"}>
            {props.embedded && <div className="border-b border-slate-200 px-4 py-4">
              {!props.companyScope && <p className="mb-3 text-sm text-slate-600">Role colors and order: <strong>{store_name}</strong></p>}
              <nav aria-label="Team structure" className="flex flex-wrap gap-2">
                {["Departments", "Roles", "Stations"].map((name) => <button key={name} type="button"
                  aria-pressed={display === name} onClick={() => setDisplay(name)}
                  className={`rounded-lg px-3 py-2 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-600 ${display === name ? "bg-violet-50 text-violet-800" : "text-slate-600 hover:bg-slate-100"}`}
                >{name}</button>)}
              </nav>
            </div>}

            {!props.embedded && <div>
              <TopBarDisplayTitle
                title={display}
                storeOptions={props.storeOptions}
                homeStore={props.homeStore}
                storeNames={props.storeNames}
                change_home_store={props.change_home_store}
              />
            </div>}
            {display === "Departments" ? (
              <DepartmentSettingsDisplay
                departments={data["departments"]}
                roles={data["roles"]}
                isSavingData={isSavingData}
                edit_department={edit_department}
                get_department_delete_impact={get_department_delete_impact}
                store_name={store_name}
                permissionRole={props.permissionRole}
              />
            ) : display === "Roles" ? (
              <RoleSettingsDisplay
                departments={data["departments"]}
                roles={data["roles"]}
                stations={data["stations"]}
                homeStore={props.homeStore}
                isSavingData={isSavingData}
                edit_role={edit_role}
                companyScope={props.companyScope}
                role_set_order={props.companyScope ? undefined : role_set_order}
                get_role_delete_impact={get_role_delete_impact}
                store_name={store_name}
                permissionRole={props.permissionRole}
                posRoles={posRoles}
                excludedPosRoles={data?.posRoles?.redux?.payload?.excluded_pos_roles || []}
                companyId={effectiveCompanyId}
                onMappingsSaved={() => fetch_data(default_api_calls, api_params, false)}
                create_role_from_pos_role={create_role_from_pos_role}
                link_role_from_pos={link_role_from_pos}
                archive_role_from_pos={archive_role_from_pos}
                autoOpenImportModal={openRoleImportModal}
                onAutoOpenHandled={() => setOpenRoleImportModal(false)}
              />
            ) : display === "Stations" ? (
              <StationSettingsDisplay
                stations={data["stations"]}
                roles={data["roles"]}
                isSavingData={isSavingData}
                edit_station={edit_station}
                store_name={store_name}
                permissionRole={props.permissionRole}
              />
            ) : (
              <div></div>
            )}
          </div>
        </div>
      ) : (
        <div
          style={props.embedded ? { padding: "32px", display: "flex", justifyContent: "center" } : {
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <LoadSpinner />
        </div>
      )}
    </>
  );
}

export default DepartmentRoleStationSettingsDisplay;
